import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircleReply } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { hasEmoji } from '@/shared/utils/emoji';

import { useReplyPatientInquiry } from '../hooks/use-patient-inquiry-mutations';
import { usePatientInquiryQuery } from '../hooks/use-patient-inquiries';
import { usePatientInquiryDetailStore } from '../store/patient-inquiry-detail.store';
import {
  PatientInquiryApiError,
  type PatientInquiryDetail,
  type PatientInquiryGender,
} from '../types/patient-inquiry.types';
import {
  formatBirthDate,
  formatInquiredAt,
  formatInquirerUid,
} from '../utils/format-patient-inquiry';
import { maskName } from '@/features/patients/utils/format-patient';
import { PatientInquiryStatusBadge } from './PatientInquiryStatusBadge';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

// PRD §3.3: 480 px wide, opens on row click. Read-only inquiry detail + reply
// thread + reply composer. Reply/additional-reply gated by CUSTOMER_SUPPORT:WRITE.
const PANEL_WIDTH = 480;
const REPLY_MAX = 2000;

const MUTED: React.CSSProperties = { color: 'var(--muted-foreground)' };

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      <div style={{ width: 80, ...MUTED }}>{label}</div>
      <div style={{ flex: 1 }}>{value}</div>
    </div>
  );
}

// Read-only inquiry header (PRD §3.3 step 3). Withdrawn inquirers hide
// gender/DOB and show the withdrawn label (PRD §8.3). A failed patient lookup
// (PRD §3.1 / §8.2) keeps the inquiry visible but replaces the identity with the
// patient-info error and hides gender/DOB — distinct from a withdrawal.
// (Inquirer phone is not part of the PRD 12 §3.3 detail — removed.)
function InquiryInfo({ inquiry }: { inquiry: PatientInquiryDetail }) {
  const { t } = useTranslation('admin');
  const genderLabels: Record<PatientInquiryGender, string> = {
    M: t('patientInquiry.genders.M'),
    F: t('patientInquiry.genders.F'),
  };
  const lookupFailed = Boolean(inquiry.patientLookupFailed);
  const hideIdentity = lookupFailed || inquiry.isWithdrawn;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        fontSize: 14,
      }}
    >
      <InfoRow
        label={t('patientInquiry.panel.fields.uid')}
        value={formatInquirerUid(inquiry.uid)}
      />
      <InfoRow
        label={t('patientInquiry.panel.fields.inquirer')}
        value={
          lookupFailed ? (
            <span style={{ color: 'var(--destructive)' }}>
              {t('patientInquiry.error.patientInfo')}
            </span>
          ) : inquiry.isWithdrawn ? (
            <span style={MUTED}>{t('patientInquiry.withdrawn')}</span>
          ) : (
            <span style={{ fontWeight: 600 }}>{maskName(inquiry.inquirerName)}</span>
          )
        }
      />
      {!hideIdentity && (
        <>
          <InfoRow
            label={t('patientInquiry.panel.fields.gender')}
            value={inquiry.gender ? genderLabels[inquiry.gender] : '-'}
          />
          <InfoRow
            label={t('patientInquiry.panel.fields.birthDate')}
            value={formatBirthDate(inquiry.birthDate)}
          />
        </>
      )}
      <InfoRow
        label={t('patientInquiry.panel.fields.type')}
        value={
          <span className="badge badge-outline">
            {t(`patientInquiry.types.${inquiry.type}`)}
          </span>
        }
      />
      <InfoRow
        label={t('patientInquiry.panel.fields.inquiredAt')}
        value={<span style={MUTED}>{formatInquiredAt(inquiry.inquiredAt)}</span>}
      />
    </div>
  );
}

// PRD §3.3 / §4.3: reply thread, oldest → newest.
function ReplyThread({ inquiry }: { inquiry: PatientInquiryDetail }) {
  const { t } = useTranslation('admin');
  if (inquiry.replies.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--space-4)',
          border: '1px dashed var(--border)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 13,
          ...MUTED,
        }}
      >
        {t('patientInquiry.panel.noReplies')}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {inquiry.replies.map((reply) => (
        <div
          key={reply.id}
          style={{
            padding: 'var(--space-4)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--muted)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, ...MUTED }}
          >
            <span>{t('patientInquiry.panel.replyAuthor', { author: reply.author })}</span>
            <span>
              {t('patientInquiry.panel.replyCreatedAt', {
                createdAt: formatInquiredAt(reply.createdAt),
              })}
            </span>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {reply.content}
          </div>
        </div>
      ))}
    </div>
  );
}

// Reply composer textarea + char counter + inline error (PRD §3.3 / §4.2 / §8.1).
function ReplyComposer({
  value,
  error,
  placeholder,
  disabled,
  onChange,
  ...rest
}: {
  value: string;
  error: string | null;
  placeholder: string;
  disabled: boolean;
  onChange: (next: string) => void;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'>) {
  return (
    <div className="form-field" style={{ display: 'flex', flexDirection: 'column', margin: 0 }}>
      <textarea
        className={`form-field__textarea${error ? ' form-field__textarea--error' : ''}`}
        {...rest}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ minHeight: 160, resize: 'none' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <p className="form-field__error" style={{ margin: 0 }}>{error ?? ''}</p>
        <div style={{ fontSize: 12, ...MUTED }}>
          {value.length.toLocaleString()} / 2,000
        </div>
      </div>
    </div>
  );
}

function PanelBody({ inquiry }: { inquiry: PatientInquiryDetail }) {
  const { t } = useTranslation('admin');
  const { canWrite, denyProps, tooltip } = useWriteGuard('CUSTOMER_SUPPORT');
  const closePanel = usePatientInquiryDetailStore((s) => s.close);
  const requestClose = usePatientInquiryDetailStore((s) => s.requestClose);
  const setDirty = usePatientInquiryDetailStore((s) => s.setDirty);
  const replyMutation = useReplyPatientInquiry();

  const answered = inquiry.status === 'ANSWERED';

  const [replyLocal, setReplyLocal] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  // ANSWERED inquiries hide the composer until [추가 답변] is clicked (PRD §3.3).
  const [composing, setComposing] = useState(false);

  // Reset composer state when the panel switches to a different inquiry.
  useEffect(() => {
    setReplyLocal('');
    setReplyError(null);
    setComposing(false);
  }, [inquiry.id]);

  // Unsaved-changes guard: a non-empty reply draft marks the panel dirty so a
  // close (footer Cancel / click-outside / Esc) confirms before discarding.
  useEffect(() => {
    setDirty(replyLocal.trim().length > 0);
  }, [replyLocal, setDirty]);

  const handleChange = (next: string) => {
    if (next.length > REPLY_MAX) {
      // PRD §4.2 / §8.1: hard-trim at 2,000 + inline error.
      setReplyLocal(next.slice(0, REPLY_MAX));
      setReplyError(t('patientInquiry.validation.replyTooLong'));
      return;
    }
    setReplyLocal(next);
    if (replyError) setReplyError(null);
  };

  const handleSend = async () => {
    if (replyMutation.isPending) return;
    const content = replyLocal.trim();
    if (!content) {
      setReplyError(t('patientInquiry.validation.replyRequired'));
      return;
    }
    // SHCT-857 — 답변 본문 is a BODY-class field, and [공통] 00 §4.2 bans 이모지 for
    // every class. Checked at send (not per keystroke) so a mid-typing paste does
    // not fight the length trimming above.
    if (hasEmoji(content)) {
      setReplyError(t('common:errors.emojiNotAllowed'));
      return;
    }
    try {
      await replyMutation.mutateAsync({
        id: inquiry.id,
        input: { replyContent: content },
      });
      // PRD §3.3 / prototype: close panel + success toast; the list query
      // re-renders the flipped status badge via mutation invalidation.
      toast.success(
        answered
          ? t('patientInquiry.toasts.extraReplySuccess')
          : t('patientInquiry.toasts.replySuccess'),
      );
      closePanel();
    } catch (e) {
      if (e instanceof PatientInquiryApiError) {
        if (e.fields?.replyContent) {
          setReplyError(e.fields.replyContent);
          return;
        }
        toast.error(e.message);
        return;
      }
      toast.error(t('patientInquiry.toasts.replyError'));
    }
  };

  const writeTooltip = canWrite ? undefined : t('patientInquiry.tooltips.noWritePermission');
  // PENDING shows the composer inline; ANSWERED reveals it behind [추가 답변].
  const composerVisible = !answered || composing;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 'var(--space-4)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PatientInquiryStatusBadge status={inquiry.status} />
      </div>

      <InquiryInfo inquiry={inquiry} />

      <div
        style={{
          marginTop: 'var(--space-2)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--border)',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>{inquiry.title}</div>
        <div
          style={{
            background: 'var(--muted)',
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 14,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {inquiry.content}
        </div>
      </div>

      {/* Reply section */}
      <div
        style={{
          marginTop: 'var(--space-4)',
          paddingTop: 'var(--space-4)',
          borderTop: '2px solid var(--border)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <MessageCircleReply size={20} color="var(--primary)" />
            {t('patientInquiry.panel.replyHeading')}
          </span>
          {answered && !composing && (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setComposing(true)}
              disabled={!canWrite}
              {...denyProps}
              title={writeTooltip}
            >
              {t('patientInquiry.actions.addReply')}
            </button>
          )}
        </div>

        {answered && <ReplyThread inquiry={inquiry} />}

        {composerVisible && (
          // SHCT-792 — the prototype separates the ADDITIONAL-reply composer from the thread above it
          // with a dashed rule (`patExtraReplyWrap`: margin-top + padding-top + 1px dashed border).
          // Only the additional composer carries it: the first reply has no thread above it to be
          // divided from, and there it is a plain block, as here.
          <div
            style={
              answered
                ? {
                    marginTop: 'var(--space-4)',
                    paddingTop: 'var(--space-4)',
                    borderTop: '1px dashed var(--border)',
                  }
                : undefined
            }
          >
            <ReplyComposer
              value={replyLocal}
              error={replyError}
              placeholder={
                answered
                  ? t('patientInquiry.panel.extraReplyPlaceholder')
                  : t('patientInquiry.panel.replyPlaceholder')
              }
              // PRD §7 / §8.3 (US-PIM-05): READ users cannot compose — the editor
              // itself is disabled, not just the send button.
              disabled={!canWrite || replyMutation.isPending}
              {...denyProps}
              onChange={handleChange}
            />
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={() => requestClose()}
          disabled={replyMutation.isPending}
        >
          {t('patientInquiry.actions.cancel')}
        </button>
        {composerVisible && (
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={handleSend}
            disabled={!canWrite || replyMutation.isPending}
            {...denyProps}
            title={writeTooltip}
          >
            {replyMutation.isPending
              ? t('patientInquiry.actions.sending')
              : answered
                ? t('patientInquiry.actions.submitExtraReply')
                : t('patientInquiry.actions.sendReply')}
          </button>
        )}
      </div>
    </div>
  );
}

function PanelContent({ id }: { id: number }) {
  const { t } = useTranslation('admin');
  const { data, isLoading, error, refetch } = usePatientInquiryQuery(id);

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', ...MUTED }}>
        {t('patientInquiry.panel.loading')}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div
        role="alert"
        style={{
          padding: 16,
          border: '1px solid var(--destructive)',
          borderRadius: 8,
          background: '#FEE2E8',
          color: 'var(--destructive)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>{t('patientInquiry.error.detail')}</span>
        <button type="button" className="btn btn-sm btn-outline" onClick={() => void refetch()}>
          {t('patientInquiry.actions.retry')}
        </button>
      </div>
    );
  }
  return <PanelBody inquiry={data} />;
}

export function PatientInquiryDetailPanel() {
  const { t } = useTranslation('admin');
  const openId = usePatientInquiryDetailStore((s) => s.openId);
  const closePanel = usePatientInquiryDetailStore((s) => s.close);
  const requestClose = usePatientInquiryDetailStore((s) => s.requestClose);
  const showDiscard = usePatientInquiryDetailStore((s) => s.showDiscard);
  const cancelDiscard = usePatientInquiryDetailStore((s) => s.cancelDiscard);

  return (
    <>
      <SlidePanel
        open={openId != null}
        title={t('patientInquiry.panel.title')}
        width={PANEL_WIDTH}
        onClose={requestClose}
      >
        {openId != null && <PanelContent id={openId} />}
      </SlidePanel>

      {/* Unsaved reply-draft guard (shared wording, SHCT-448). */}
      <ConfirmModal
        open={showDiscard}
        title={t('patientInquiry.discard.title')}
        message={t('patientInquiry.discard.message')}
        confirmLabel={t('patientInquiry.discard.confirm')}
        cancelLabel={t('patientInquiry.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={closePanel}
        onCancel={cancelDiscard}
      />
    </>
  );
}
